/* Shared visibility controller. Purchase actions remain owned by their original form/controller. */
(() => {
  'use strict';
  if (customElements.get('lpl-sticky-atc')) return;
  const instances = new Set();
  let owner;
  const overlaySelector = '#lpl-contextual-popup-root,[data-lpl-ia-modal],cart-drawer,cart-notification,#cart-notification,modal-dialog,product-modal,dialog,header-drawer details,menu-drawer details,details-modal details,.mwp__modal-with-pages,.lpl-ing-facts-modal,[aria-modal="true"],[role="dialog"]';
  // Yield only to actual purchase controls, explicit page markers and the footer.
  // Discovery links and promotional cards can coexist with this navigation bar.
  const yieldSelector = '[data-lpl-sticky-yield],.product-form__submit,.pdp__btn-submit,.lpl-po__submit,button[data-atc-button],form[action*="/cart/add"] [type="submit"],footer.footer,.shopify-section-group-footer-group';
  // Persistent editorial rail promotions coexist with the bar; inline purchase controls still yield.
  const ignoredYield = node => node.closest('[data-lpl-sticky-ignore="seo-sidebar"]');
  const lockPattern = /^(?:overflow-hidden(?:-(?:mobile|tablet|desktop))?|lplcp-is-open|lpl-popup-open|modal-open|drawer-open)$/;
  const hiddenAncestor = node => node.closest('[hidden],[inert],[aria-hidden="true"]');
  function shown(node) {
    if (!node.isConnected || hiddenAncestor(node)) return false;
    for (let el = node; el && el !== document.body; el = el.parentElement) {
      if (el.matches('details:not([open]),modal-dialog:not([open]),product-modal:not([open]),dialog:not([open])')) return false;
      const style = getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse' || Number(style.opacity) === 0) return false;
    }
    const rect = node.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }
  const locked = () => [...document.body.classList].some(name => lockPattern.test(name));
  function activateNext() {
    if (owner) return;
    const next = [...instances].find(el => el.isConnected && el.markupReady);
    if (next) { owner = next; next.start(); }
  }
  class LplStickyAtc extends HTMLElement {
    connectedCallback() {
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;
      this.classList.remove('is-visible', 'is-folded');
      instances.add(this);
      // The async asset follows this component's markup. Start as soon as the
      // component is complete, without waiting for unrelated deferred scripts.
      // Also handle a later instance parsed after this definition already exists.
      this.markupReady = false;
      const ready = () => {
        if (!this.isConnected || !this.querySelector('.lpl-satc__inner,.lpl-s3__in') || !this.querySelector('.lpl-satc__btn,.lpl-satc__action,.lpl-s3__btn')) return;
        this.markupReady = true;
        this.markupObserver?.disconnect();
        activateNext();
      };
      ready();
      if (!this.markupReady) {
        this.markupObserver = new MutationObserver(ready);
        this.markupObserver.observe(this, { childList: true, subtree: true });
      }
    }
    disconnectedCallback() {
      instances.delete(this);
      this.markupObserver?.disconnect();
      this.markupReady = false;
      if (owner !== this) return;
      this.stop();
      owner = null;
      activateNext();
    }
    start() {
      this.abort = new AbortController();
      this.raf = 0;
      this.past = false;
      this.visible = false;
      this.reserve = 0;
      this.height = this.getBoundingClientRect().height;
      this.overlays = new Set();
      this.yields = new Set();
      this.intersections = new Set();
      this.overlayDirty = true;
      this.bodyLocked = locked();
      this.blocked = false;
      this.basePadding = parseFloat(getComputedStyle(document.body).paddingBottom) || 0;
      this.links = [...this.querySelectorAll('a.lpl-satc__link,a.lpl-s3')];
      this.linkTabindexes = new Map(this.links.map(link => [link, link.getAttribute('tabindex')]));
      this.layoutHost = this.querySelector('[data-lpl-layout-host]');
      if (this.layoutHost) { this.layoutObserver = new MutationObserver(() => this.schedule()); this.layoutObserver.observe(this.layoutHost, { attributes: true, attributeFilter: ['data-lpl-arm'] }); }
      this.savedClasses = new Map(['lpl-satc-on', 'lpl-satc-float', 'lpl-satc-reserved'].map(name => [name, document.body.classList.contains(name)]));
      this.savedVars = new Map(['--lpl-satc-h', '--lpl-satc-reserve-h'].map(name => [name, [document.documentElement.style.getPropertyValue(name), document.documentElement.style.getPropertyPriority(name)]]));
      this.writtenVars = new Map();
      this.isFloating = () => this.classList.contains('lpl-satc--float') && innerWidth >= 990;
      const listen = (node, name, callback, options = {}) => node.addEventListener(name, callback, { ...options, signal: this.abort.signal });
      const queue = () => this.schedule();
      const dirty = () => { this.overlayDirty = true; this.schedule(); };
      listen(window, 'scroll', queue, { passive: true });
      listen(window, 'resize', () => { this.measureEntry(); dirty(); }, { passive: true });
      listen(window, 'pageshow', () => { this.measureEntry(); dirty(); });
      // Reconcile anything parsed after this bar; readiness is a refresh, not a gate.
      listen(document, 'DOMContentLoaded', () => { this.collectOnFrame = true; dirty(); }, { once: true });
      listen(window, 'load', () => { this.measureEntry(); dirty(); }, { once: true });
      listen(document, 'visibilitychange', dirty);
      listen(document, 'focusin', dirty);
      listen(document, 'focusout', dirty);
      listen(document, 'toggle', dirty, { capture: true });
      listen(window, 'lpl:contextual-popup', dirty);
      if (window.visualViewport) listen(visualViewport, 'resize', dirty, { passive: true });
      listen(document, 'shopify:section:load', event => { this.collect(event.target); this.measureEntry(); dirty(); });
      listen(document, 'shopify:section:unload', () => { this.prune(); dirty(); });
      this.overlayObserver = new MutationObserver(dirty);
      this.bodyObserver = new MutationObserver(records => {
        let changed = false;
        const state = locked();
        if (state !== this.bodyLocked) { this.bodyLocked = state; changed = true; }
        records.forEach(record => {
          if (record.type === 'childList') {
            record.addedNodes.forEach(node => {
              if (node.nodeType === 1 && !node.matches('script,style,lpl-sticky-atc')) { this.collect(node); changed = true; }
            });
            if ([...record.removedNodes].some(node => node.nodeType === 1 && (node.matches(overlaySelector) || node.querySelector(overlaySelector)))) { this.prune(); changed = true; }
          }
        });
        if (changed) dirty();
      });
      this.bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'], childList: true });
      if ('IntersectionObserver' in window) {
        this.yieldObserver = new IntersectionObserver(entries => {
          this.yieldReady = true;
          entries.forEach(entry => entry.isIntersecting ? this.intersections.add(entry.target) : this.intersections.delete(entry.target));
          this.schedule();
        }, { rootMargin: '-40px 0px -80px 0px', threshold: 0 });
        this.entryObserver = new IntersectionObserver(() => { this.measureEntry(); this.schedule(); }, { threshold: [0, 1] });
      }
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(entries => {
          const entry = entries.find(item => item.target === this);
          if (entry) this.height = entry.borderBoxSize?.[0]?.blockSize || this.getBoundingClientRect().height;
          this.schedule();
        });
        this.resizeObserver.observe(this);
      }
      // Scan the currently parsed controls on the first frame. DOM readiness
      // refreshes this list later without delaying the first visibility decision.
      this.collectOnFrame = true;
      this.yieldReady = !this.yieldObserver;
      this.findEntry();
      this.measureEntry();
      this.schedule();
    }
    findEntry() {
      const main = document.querySelector('main') || document;
      let entry;
      this.automaticEntry = false;
      if (this.dataset.entry) { try { entry = document.querySelector(this.dataset.entry); } catch (_) { /* Invalid merchant selector uses the finite intro fallback. */ } }
      if (!entry) entry = main.querySelector('[data-lpl-sticky-entry]');
      if (!entry) {
        const heading = main.querySelector('h1');
        const candidate = heading?.closest('header') || heading?.closest('.shopify-section');
        if (candidate && candidate.getBoundingClientRect().height <= innerHeight * 1.5) {
          entry = candidate;
          this.automaticEntry = true;
        }
      }
      this.entry = entry && !this.contains(entry) ? entry : null;
      if (this.entry) this.entryObserver?.observe(this.entry);
    }
    measureEntry() {
      if (this.entry && !this.entry.isConnected) { this.entryObserver?.unobserve(this.entry); this.findEntry(); }
      const rect = this.entry?.getBoundingClientRect();
      // A tool section can grow after initial parsing. It must not turn the
      // finite intro trigger into a requirement to finish the entire tool/page.
      const finiteEntry = rect && (!this.automaticEntry || rect.height <= innerHeight * 1.5);
      this.past = finiteEntry ? rect.bottom <= 0 : Math.max(0, scrollY) >= innerHeight * .85;
    }
    collect(root) {
      const query = selector => [...(root.matches?.(selector) ? [root] : []), ...root.querySelectorAll(selector)];
      query(overlaySelector).forEach(node => {
        if (node.closest('lpl-sticky-atc') || this.overlays.has(node)) return;
        this.overlays.add(node);
        this.overlayObserver.observe(node, { attributes: true, attributeFilter: ['class', 'open', 'hidden', 'inert', 'aria-hidden', 'aria-modal', 'style'] });
        // A dialog panel may retain aria-modal inside a hidden outer shell.
        for (let parent = node.parentElement; parent && parent !== document.body; parent = parent.parentElement) {
          this.overlayObserver.observe(parent, { attributes: true, attributeFilter: ['class', 'open', 'hidden', 'inert', 'aria-hidden', 'style'] });
        }
      });
      query(yieldSelector).forEach(node => {
        if (node.closest('lpl-sticky-atc,#sticky-atc-bar,[data-lpl-mobile-sticky]') || ignoredYield(node) || this.yields.has(node)) return;
        this.yields.add(node);
        this.yieldObserver?.observe(node);
        // An empty footer wrapper can already intersect before its content is
        // parsed. Its later height change need not produce another IO callback.
        if (node.matches('footer.footer,.shopify-section-group-footer-group')) this.resizeObserver?.observe(node);
      });
      this.prune();
    }
    prune() {
      this.yields.forEach(node => { if (!node.isConnected) { this.yieldObserver?.unobserve(node); this.resizeObserver?.unobserve(node); this.yields.delete(node); this.intersections.delete(node); } });
      this.overlays.forEach(node => { if (!node.isConnected) this.overlays.delete(node); });
    }
    overlayOpen() {
      if (locked()) return true;
      const viewport = window.visualViewport;
      if (viewport && Math.abs(viewport.scale - 1) < .05 && innerHeight - viewport.height > 150) return true;
      const active = document.activeElement;
      if (active && !this.contains(active) && active.matches('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]),textarea,select,[contenteditable]:not([contenteditable="false"])')) return true;
      for (const node of this.overlays) {
        if (!node.isConnected) continue;
        if (node.matches('cart-drawer,cart-notification,#cart-notification') && !node.matches('.active,.is-open')) continue;
        if (node.closest('cart-drawer:not(.active):not(.is-open),cart-notification:not(.active):not(.is-open)')) continue;
        if (node.matches('.mwp__modal-with-pages.is-hide-modal-with-pages')) continue;
        if (node.closest('.mwp__modal-with-pages.is-hide-modal-with-pages')) continue;
        // Keep NAD blocked through its closing animation/background-inert cleanup.
        if (node.id === 'lpl-contextual-popup-root' && !node.hidden) return true;
        if (shown(node)) return true;
      }
      return false;
    }
    yielding() {
      // Use current geometry until the observer delivers its first result;
      // a delayed observer must neither stall the bar nor expose it over a form.
      const nodes = this.yieldObserver && this.yieldReady ? this.intersections : this.yields;
      for (const node of nodes) {
        if (ignoredYield(node) || !shown(node)) continue;
        const rect = node.getBoundingClientRect();
        if (rect.bottom > 40 && rect.top < innerHeight - 80) return true;
      }
      return false;
    }
    schedule() {
      if (this.raf || owner !== this || !this.isConnected) return;
      this.raf = requestAnimationFrame(() => { this.raf = 0; this.update(); });
    }
    update() {
      if (owner !== this || !this.isConnected) return;
      if (this.collectOnFrame) {
        this.collectOnFrame = false;
        this.collect(document);
        if (!this.yieldObserver || !this.yields.size) this.yieldReady = true;
      }
      // The fallback has no observed element; explicit entries only need one small rect read.
      this.measureEntry();
      if (this.overlayDirty) { this.blocked = this.overlayOpen(); this.overlayDirty = false; }
      const eligible = this.past && !this.blocked && document.visibilityState === 'visible' && !this.yielding();
      // A single resting layout keeps the bar's size stable in either scroll direction.
      if (!this.past) this.reserve = 0;
      this.classList.toggle('is-visible', eligible);
      if (this.visible !== eligible || this.getAttribute('aria-hidden') !== String(!eligible)) {
        this.setAttribute('aria-hidden', String(!eligible));
        this.inert = !eligible;
        this.syncLinkFocus(eligible);
      }
      this.syncLinkFocus(eligible);
      this.visible = eligible;
      if (!this.resizeObserver) this.height = this.getBoundingClientRect().height;
      const covered = this.height + (this.isFloating() ? 16 : 0);
      if (eligible) this.reserve = Math.max(this.reserve, covered);
      this.setVar('--lpl-satc-h', (eligible ? covered : 0) + 'px');
      this.setVar('--lpl-satc-reserve-h', (this.basePadding + this.reserve) + 'px');
      document.body.classList.toggle('lpl-satc-on', eligible);
      document.body.classList.toggle('lpl-satc-float', eligible && this.isFloating());
      document.body.classList.toggle('lpl-satc-reserved', this.reserve > 0);
    }
    syncLinkFocus(eligible) {
      const arm = this.layoutHost?.dataset.lplArm === 'B' ? 'B' : 'A';
      this.links.forEach(link => { const visible = eligible && (!link.dataset.lplArmPane || link.dataset.lplArmPane === arm); const value = visible ? '0' : '-1'; if (link.getAttribute('tabindex') !== value) link.setAttribute('tabindex', value); });
    }
    setVar(name, value) {
      if (this.writtenVars.get(name) === value) return;
      this.writtenVars.set(name, value);
      document.documentElement.style.setProperty(name, value);
    }
    stop() {
      this.abort?.abort();
      cancelAnimationFrame(this.raf);
      this.entryObserver?.disconnect();
      this.yieldObserver?.disconnect();
      this.resizeObserver?.disconnect();
      this.overlayObserver?.disconnect();
      this.bodyObserver?.disconnect();
      this.layoutObserver?.disconnect();
      this.classList.remove('is-visible', 'is-folded');
      this.setAttribute('aria-hidden', 'true');
      this.inert = true;
      this.linkTabindexes?.forEach((value, link) => value === null ? link.removeAttribute('tabindex') : link.setAttribute('tabindex', value));
      this.savedClasses?.forEach((present, name) => document.body.classList.toggle(name, present));
      this.savedVars?.forEach(([value, priority], name) => {
        if (document.documentElement.style.getPropertyValue(name) !== this.writtenVars.get(name)) return;
        value ? document.documentElement.style.setProperty(name, value, priority) : document.documentElement.style.removeProperty(name);
      });
    }
  }
  customElements.define('lpl-sticky-atc', LplStickyAtc);
})();
